import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { API_BASE, publicMessageOf, useTRPC } from '../lib/trpc';
import { DataTable, type Column } from '../components/DataTable';
import { ExportButton } from '../components/ExportButton';
import classes from './EntityPage.module.css';

interface KeywordRow {
  id: string;
  ownerType: string;
  ownerId: string;
  keyword: string;
  geoId: string;
  countryCode: string;
  language: string;
  source: string;
  volume: number | null;
  views: number | null;
  positionDeci: number | null;
  aiStatus: string;
  priority: number;
  urlPattern: string | null;
  pageBindingId: string | null;
}

interface UploadResult {
  fileKey: string;
  headers: string[];
  sheet: string | null;
  sheetNames: string[];
  rowCount: number;
  sample: Record<string, string>[];
  suggestedMapping: Record<string, string | undefined>;
}

interface ImportReport {
  rowsTotal: number;
  rowsNew: number;
  rowsDuplicate: number;
  rowsRejected: number;
  newPages: number;
  rejections: { row: number; keyword: string; reason: string }[];
}

/** The wizard's mapping fields, in the order the operator confirms them. */
const MAPPABLE = [
  { field: 'keyword', label: 'Ключ', required: true },
  { field: 'volume', label: 'Обʼєм', required: false },
  { field: 'position', label: 'Позиція', required: false },
  { field: 'country', label: 'Країна', required: false },
  { field: 'language', label: 'Мова', required: false },
  { field: 'urlPattern', label: 'URL', required: false },
] as const;

const REJECTION_LABELS: Record<string, string> = {
  no_keyword: 'порожній ключ',
  no_geo: 'немає ГЕО і не задано за замовчуванням',
  unknown_geo: 'країна, у якій сітка не працює',
  language_not_in_geo: 'мова, якої немає в цьому ГЕО',
  duplicate_in_file: 'дубль усередині файлу',
  below_min_volume: 'обʼєм нижчий за мінімальний',
};

const AI_STATUS_LABELS: Record<string, string> = {
  ok: 'ок',
  new_page: 'потрібна сторінка',
  duplicate: 'дубль',
  cannibalization: 'канібалізація',
  no_geo: 'без ГЕО',
};

/**
 * Ядро — keywords (§1.6), acceptance scenarios 5 and 6.
 *
 * The whole screen exists to enforce one rule: there is no keyword without a (geo, language)
 * pair. Both ways in — a spreadsheet and Search Console — go through the same import, so the
 * duplicate report and the rejection reasons are identical whichever route the operator took.
 */
export function CorePage(): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  /**
   * Власник може прийти з вкладки «Ключі» своєї картки — тоді вибирати його вдруге не треба.
   *
   * Тип власника теж: ядро мають не лише бренди. `keywords` тримає `owner_type` з чотирьох
   * значень, а цей екран знав рівно одне — тобто ключі гри чи кластера не імпортувались
   * ніяк, і в реєстрі їх могло не бути взагалі. Спільний екран, який уміє одну сутність із
   * трьох, — це не спільний екран.
   */
  const search_ = useSearch({ from: '/authed/core' });
  const [ownerType, setOwnerType] = useState<'brand' | 'game' | 'cluster'>(
    search_.ownerType ?? 'brand',
  );
  const [ownerId, setOwnerId] = useState<string | null>(search_.ownerId ?? null);
  const [search, setSearch] = useState('');
  const [unboundOnly, setUnboundOnly] = useState(false);

  const [upload, setUpload] = useState<UploadResult | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [mapping, setMapping] = useState<Record<string, string | null>>({});
  const [defaultGeoId, setDefaultGeoId] = useState<string | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  /** Який із минулих імпортів розгорнутий; `null` — жоден. */
  const [openImportId, setOpenImportId] = useState<string | null>(null);
  /** Ручне додавання ключа — «+ КЕЙВОРД» мокапа: один рядок, а не файл. */
  const [manualKeyword, setManualKeyword] = useState('');
  const [manualGeoId, setManualGeoId] = useState<string | null>(null);
  const [manualLanguage, setManualLanguage] = useState('');

  const [gscLanguage, setGscLanguage] = useState<string>('fr');
  const [gscGeoId, setGscGeoId] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const pagination = { limit: 200, direction: 'asc' as const };
  const brands = useQuery({
    ...trpc.brands.list.queryOptions({ pagination }),
    enabled: ownerType === 'brand',
  });
  const games = useQuery({
    ...trpc.games.list.queryOptions({ pagination }),
    enabled: ownerType === 'game',
  });
  const clusters = useQuery({
    ...trpc.clusters.list.queryOptions({ pagination }),
    enabled: ownerType === 'cluster',
  });
  const geos = useQuery(trpc.geo.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }));

  const owner = ownerId === null ? null : { ownerType, ownerId };

  const list = useQuery({
    ...trpc.keywords.list.queryOptions({
      ...(owner ?? {}),
      ...(search ? { search } : {}),
      ...(unboundOnly ? { unboundOnly: true } : {}),
      pagination: { limit: 200, direction: 'asc' },
    }),
    enabled: ownerId !== null,
  });

  const summary = useQuery({
    ...trpc.keywords.summary.queryOptions({ ...(owner ?? {}) }),
    enabled: ownerId !== null,
  });

  /** Історія наповнення цього ядра — секція «Джерела» мокапа. */
  const imports = useQuery({
    ...trpc.keywords.imports.list.queryOptions({
      ...(owner ?? {}),
      pagination: { limit: 20, direction: 'desc' },
    }),
    enabled: ownerId !== null,
  });

  const openImport = useQuery({
    ...trpc.keywords.imports.byId.queryOptions({ id: openImportId ?? '' }),
    enabled: openImportId !== null,
  });

  const candidates = useQuery({
    ...trpc.keywords.gscCandidates.queryOptions({
      ownerType,
      ownerId: ownerId ?? '',
      propertyUrl: 'https://nordicspin-fr.example/',
      geoId: gscGeoId ?? '',
      language: gscLanguage,
      startDate: '2026-05-16',
      endDate: '2026-08-13',
      limit: 100,
    }),
    enabled: ownerId !== null && gscGeoId !== null,
  });

  const invalidateCore = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.keywords.pathKey() });
  };

  const commit = useMutation(
    trpc.keywords.imports.commit.mutationOptions({
      onSuccess: async (result: unknown) => {
        setReport(result as ImportReport);
        setUpload(null);
        await invalidateCore();
      },
    }),
  );

  /**
   * «+ КЕЙВОРД» і видалення рядка — дві процедури, що існували без викликача.
   *
   * Ядро наповнювалось лише файлом і Search Console: додати один ключ руками або прибрати
   * помилковий було нічим, хоча обидві дії є в мокапі й обидві потрібні щодня — саме так
   * ядро й доводять після імпорту.
   */
  const addKeyword = useMutation(
    trpc.keywords.add.mutationOptions({
      onSuccess: async () => {
        setManualKeyword('');
        await invalidateCore();
      },
    }),
  );

  const removeKeyword = useMutation(
    trpc.keywords.remove.mutationOptions({ onSuccess: invalidateCore }),
  );

  const fromGsc = useMutation(
    trpc.keywords.fromGsc.mutationOptions({
      onSuccess: async (result: unknown) => {
        setReport(result as ImportReport);
        setPicked(new Set());
        await invalidateCore();
      },
    }),
  );

  /**
   * The file goes to the API, not straight to S3: the wizard's next screen is the column
   * mapping, which needs the headers the server just read.
   */
  const onFile = async (file: File): Promise<void> => {
    setUploading(true);
    setUploadError(null);
    setReport(null);
    try {
      const body = new FormData();
      body.append('file', file);
      const res = await fetch(`${API_BASE}/imports/upload`, {
        method: 'POST',
        body,
        credentials: 'include',
      });
      const json = (await res.json()) as UploadResult & { message?: string; error?: string };
      if (!res.ok) {
        setUploadError(json.message ?? json.error ?? 'Не вдалося прочитати файл.');
        return;
      }
      setUpload(json);
      setMapping(
        Object.fromEntries(
          MAPPABLE.map((m) => [m.field, json.suggestedMapping[m.field] ?? null]),
        ),
      );
    } catch (err) {
      setUploadError(publicMessageOf(err));
    } finally {
      setUploading(false);
    }
  };

  const columns: Column<KeywordRow>[] = [
    { key: 'keyword', header: 'Ключ', render: (row) => <span>{row.keyword}</span> },
    {
      key: 'geo',
      header: 'ГЕО · мова',
      render: (row) => (
        <Group gap={6}>
          <Badge size="sm" variant="default" data-testid={`kw-geo-${row.keyword}`}>
            {row.countryCode}
          </Badge>
          <span className={classes.mono}>{row.language}</span>
        </Group>
      ),
    },
    {
      key: 'volume',
      header: 'Обʼєм',
      align: 'right',
      render: (row) => (
        <span className={classes.mono} data-testid={`kw-volume-${row.keyword}`}>
          {row.volume ?? '—'}
        </span>
      ),
    },
    {
      key: 'position',
      header: 'Позиція',
      align: 'right',
      render: (row) => (
        <span className={classes.mono}>
          {row.positionDeci === null ? '—' : (row.positionDeci / 10).toFixed(1)}
        </span>
      ),
    },
    {
      key: 'source',
      header: 'Джерело',
      render: (row) => (
        <Badge size="sm" variant="default" data-testid={`kw-source-${row.keyword}`}>
          {row.source}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Статус',
      render: (row) => (
        <Badge
          size="sm"
          color={row.aiStatus === 'ok' ? 'mint' : 'amber'}
          data-testid={`kw-status-${row.keyword}`}
        >
          {AI_STATUS_LABELS[row.aiStatus] ?? row.aiStatus}
        </Badge>
      ),
    },
    {
      /*
       * Прибрати помилковий рядок — друга половина ручного ведення ядра. `keywords.remove`
       * приймає перелік, тож той самий виклик обслуговуватиме й масове прибирання, коли воно
       * знадобиться; поки що — один рядок, як у мокапі.
       */
      key: 'actions',
      header: '',
      align: 'right',
      render: (row) => (
        <Button
          size="compact-xs"
          variant="subtle"
          loading={removeKeyword.isPending}
          data-testid={`kw-remove-${row.keyword}`}
          onClick={() => {
            removeKeyword.mutate({ ids: [row.id] });
          }}
        >
          прибрати
        </Button>
      ),
    },
  ];

  const ownerRows =
    ownerType === 'brand'
      ? (brands.data?.items ?? [])
      : ownerType === 'game'
        ? (games.data?.items ?? [])
        : (clusters.data?.items ?? []);
  const ownerOptions = ownerRows.map((row) => ({ value: row.id, label: row.name }));
  const geoOptions = (geos.data?.items ?? []).map((g) => ({
    value: g.id,
    label: `${g.countryCode} · ${g.countryName}`,
  }));

  return (
    <Stack gap="md">
      <Group gap="md" align="flex-end">
        <Select
          label="Тип"
          data={[
            { value: 'brand', label: 'Бренд' },
            { value: 'game', label: 'Гра' },
            { value: 'cluster', label: 'Кластер' },
          ]}
          value={ownerType}
          onChange={(value) => {
            // Зміна типу скидає вибір: ідентифікатор бренда в списку ігор нічого не означає,
            // а мовчазно лишений — показав би порожнє ядро як «ключів немає».
            setOwnerType(value ?? 'brand');
            setOwnerId(null);
          }}
          data-testid="core-owner-type"
          style={{ width: 150 }}
        />
        <Select
          label="Сутність"
          placeholder={ownerType === 'brand' ? 'бренд' : ownerType === 'game' ? 'гра' : 'кластер'}
          data={ownerOptions}
          value={ownerId}
          onChange={setOwnerId}
          searchable
          data-testid="core-owner"
          style={{ width: 240 }}
        />
        <TextInput
          label="Пошук"
          placeholder="ключ…"
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
          }}
          data-testid="core-search"
          style={{ width: 220 }}
        />
        <Checkbox
          label="Лише без сторінки"
          checked={unboundOnly}
          onChange={(e) => {
            setUnboundOnly(e.currentTarget.checked);
          }}
          data-testid="core-unbound-only"
        />
      </Group>

      {/*
        * «+ КЕЙВОРД» — ручне додавання одного ключа.
        *
        * Ядро наповнювалось лише файлом і Search Console: додати рядок руками або прибрати
        * помилковий було нічим, хоча саме так ядро й доводять після імпорту. ГЕО й мова
        * обовʼязкові разом — це той самий унікальний індекс, на якому тримається §1.6, і
        * форма вимагає їх тут, а не дає впертись у відмову після натискання.
        */}
      {ownerId === null ? null : (
        <Group gap="sm" align="flex-end" wrap="wrap" data-testid="core-manual-add">
          <TextInput
            label="Ключ"
            placeholder="casino en ligne"
            value={manualKeyword}
            onChange={(event) => {
              setManualKeyword(event.currentTarget.value);
            }}
            data-testid="kw-manual-keyword"
            style={{ width: 240 }}
          />
          <Select
            label="ГЕО"
            searchable
            data={(geos.data?.items ?? []).map((geo) => ({
              value: geo.id,
              label: `${geo.countryCode} · ${geo.countryName}`,
            }))}
            value={manualGeoId}
            onChange={(value) => {
              setManualGeoId(value);
              const preset = (geos.data?.items ?? []).find((geo) => geo.id === value);
              // Мова підставляється з пресету: пара «(гео, мова)» мусить бути узгодженою, і
              // друкувати її руками означає впертись у відмову там, де відповідь відома.
              if (preset) setManualLanguage(preset.primaryLanguage);
            }}
            data-testid="kw-manual-geo"
            style={{ width: 200 }}
          />
          <TextInput
            label="Мова"
            value={manualLanguage}
            onChange={(event) => {
              setManualLanguage(event.currentTarget.value);
            }}
            data-testid="kw-manual-language"
            style={{ width: 90 }}
          />
          <Button
            size="xs"
            loading={addKeyword.isPending}
            disabled={manualKeyword.trim() === '' || manualGeoId === null || manualLanguage === ''}
            data-testid="kw-manual-add"
            onClick={() => {
              addKeyword.mutate({
                ownerType,
                ownerId,
                keyword: manualKeyword.trim(),
                geoId: manualGeoId ?? '',
                language: manualLanguage,
                source: 'manual',
              });
            }}
          >
            + кейворд
          </Button>
          {addKeyword.error === null ? null : (
            <Text className={classes.danger} data-testid="kw-manual-error" role="alert">
              {publicMessageOf(addKeyword.error)}
            </Text>
          )}
        </Group>
      )}

      {summary.data ? (
        <Group gap="lg" data-testid="core-summary">
          <Text className={classes.dim}>
            усього: <span className={classes.mono} data-testid="core-total">{summary.data.total}</span>
          </Text>
          <Text className={classes.dim}>
            потрібна сторінка:{' '}
            <span className={classes.mono} data-testid="core-newpage">
              {summary.data.byStatus.new_page ?? 0}
            </span>
          </Text>
          <Text className={classes.dim}>
            без привʼязки:{' '}
            <span className={classes.mono} data-testid="core-unbound">
              {summary.data.unbound}
            </span>
          </Text>
        </Group>
      ) : null}

      {ownerId === null ? (
        <Box className={classes.panel} data-testid="core-no-owner">
          <Text className={classes.dim}>
            Оберіть сутність — ядро завжди належить бренду, грі, кластеру або сайту.
          </Text>
        </Box>
      ) : (
        <>
          <Box className={classes.panel} data-testid="core-import">
            <Text className={classes.panelTitle} mb={8}>
              Імпорт ядра — CSV або XLSX
            </Text>
            <Group gap="sm" align="flex-end">
              <input
                ref={fileInput}
                type="file"
                accept=".csv,.xlsx,text/csv"
                data-testid="core-file"
                onChange={(e) => {
                  const file = e.currentTarget.files?.[0];
                  if (file) void onFile(file);
                }}
              />
              <Select
                label="ГЕО за замовчуванням"
                placeholder="якщо у файлі немає колонки"
                data={geoOptions}
                value={defaultGeoId}
                onChange={setDefaultGeoId}
                clearable
                data-testid="core-default-geo"
                style={{ width: 240 }}
              />
            </Group>

            {uploading ? <Text className={classes.dim}>читаємо файл…</Text> : null}
            {uploadError === null ? null : (
              <Text className={classes.danger} mt={8} data-testid="core-upload-error">
                {uploadError}
              </Text>
            )}

            {upload === null ? null : (
              <Stack gap="sm" mt="sm" data-testid="core-mapping">
                <Text className={classes.dim}>
                  {upload.rowCount} рядків
                  {upload.sheet === null ? '' : ` · аркуш «${upload.sheet}»`}
                  {upload.sheetNames.length > 1
                    ? ` (у книзі ${String(upload.sheetNames.length)}, імпортується перший)`
                    : ''}
                </Text>

                <Group gap="sm" align="flex-end" wrap="wrap">
                  {MAPPABLE.map((m) => (
                    <Select
                      key={m.field}
                      label={m.label}
                      data={upload.headers.map((h) => ({ value: h, label: h }))}
                      value={mapping[m.field] ?? null}
                      onChange={(value) => {
                        setMapping((prev) => ({ ...prev, [m.field]: value }));
                      }}
                      clearable={!m.required}
                      data-testid={`map-${m.field}`}
                      style={{ width: 160 }}
                    />
                  ))}
                </Group>

                {/*
                  * «Превʼю імпорту» — секція мокапа (рядок 2367), якої не було.
                  *
                  * Екран показував лише «842 рядки» — число, яке нічого не каже про те, що саме
                  * ляже в ядро. Кнопка «ІМПОРТУВАТИ 842» при цьому термінальна: звіт про
                  * відкинуті зʼявляється **після** запису, коли решта вже всередині.
                  *
                  * Тому перші пʼять рядків показуються рівно так, як їх прочитав мапінг: ключ,
                  * ГЕО, мова. Порожня клітинка ГЕО чи мови — не косметика, а прогноз відмови:
                  * `importKeywords` вимагає пару «(geo, language)» і відкидає рядок без неї.
                  * Побачити це до натискання дешевше, ніж у звіті після.
                  */}
                {upload.sample.length === 0 ? null : (
                  <Box data-testid="core-preview">
                    <Text className={classes.panelTitle} mb={4}>
                      Превʼю імпорту
                    </Text>
                    <Table striped withRowBorders={false} verticalSpacing={2} fz="xs">
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>КЕЙВОРД</Table.Th>
                          <Table.Th>ГЕО</Table.Th>
                          <Table.Th>МОВА</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {upload.sample.map((sampleRow, index) => {
                          const cell = (field: string): string =>
                            mapping[field] === null || mapping[field] === undefined
                              ? ''
                              : (sampleRow[mapping[field]] ?? '');
                          const geoCell = cell('country') || (defaultGeoId === null ? '' : '— за замовчуванням —');
                          const langCell = cell('language');
                          return (
                            <Table.Tr key={String(index)} data-testid={`core-preview-row-${String(index)}`}>
                              <Table.Td>{cell('keyword')}</Table.Td>
                              <Table.Td>
                                {geoCell === '' ? (
                                  <Text className={classes.danger} span data-testid={`core-preview-no-geo-${String(index)}`}>
                                    буде відкинуто: немає ГЕО
                                  </Text>
                                ) : (
                                  geoCell
                                )}
                              </Table.Td>
                              <Table.Td>{langCell === '' ? '— з пресету ГЕО —' : langCell}</Table.Td>
                            </Table.Tr>
                          );
                        })}
                      </Table.Tbody>
                    </Table>
                    {upload.rowCount > upload.sample.length ? (
                      <Text className={classes.dim} mt={2}>
                        показано {upload.sample.length} із {upload.rowCount}
                      </Text>
                    ) : null}
                  </Box>
                )}

                <Group>
                  <Button
                    size="xs"
                    disabled={!mapping.keyword}
                    loading={commit.isPending}
                    data-testid="core-import-run"
                    onClick={() => {
                      commit.mutate({
                        ownerType,
                        ownerId,
                        fileKey: upload.fileKey,
                        mapping: Object.fromEntries(
                          Object.entries(mapping).filter(([, v]) => v !== null),
                        ) as { keyword: string },
                        ...(defaultGeoId ? { defaultGeoId } : {}),
                      });
                    }}
                  >
                    Імпортувати
                  </Button>
                  {commit.error === null ? null : (
                    <Text className={classes.danger} data-testid="core-import-error">
                      {publicMessageOf(commit.error)}
                    </Text>
                  )}
                </Group>
              </Stack>
            )}

            {/*
              * «Джерела» — секція мокапа (рядок 2367): чим це ядро наповнювали досі.
              *
              * Без неї імпорт — подія без історії: після перезавантаження екрана від нього не
              * лишається нічого, і питання «звідки тут ці 800 ключів і хто їх залив» не має
              * відповіді ніде в інтерфейсі. `keywords.imports.list` і `byId` існували з
              * нульовим викликачем саме тому: історія була, дверей до неї не було.
              *
              * Рядок відкриває свій звіт — той самий, що показується одразу після імпорту.
              * Причини відмов лишаються прочитними через тиждень, а не лише в ту хвилину.
              */}
            {ownerId === null ? null : (
              <Box mt="sm" data-testid="core-sources">
                <Text className={classes.panelTitle} mb={4}>
                  Джерела
                </Text>
                {imports.isPending ? (
                  <Text className={classes.dim}>читаємо історію…</Text>
                ) : (imports.data?.items ?? []).length === 0 ? (
                  <Text className={classes.dim} data-testid="core-sources-empty">
                    Це ядро ще не наповнювали — імпортів немає.
                  </Text>
                ) : (
                  <Table striped withRowBorders={false} verticalSpacing={2} fz="xs">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>КОЛИ</Table.Th>
                        <Table.Th>ДЖЕРЕЛО</Table.Th>
                        <Table.Th>РЯДКІВ</Table.Th>
                        <Table.Th>НОВИХ</Table.Th>
                        <Table.Th>ВІДХИЛЕНО</Table.Th>
                        <Table.Th />
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {(imports.data?.items ?? []).map((row) => (
                        <Table.Tr key={row.id} data-testid={`core-source-${row.id}`}>
                          <Table.Td className={classes.mono}>
                            {new Date(row.createdAt).toISOString().slice(0, 16).replace('T', ' ')}
                          </Table.Td>
                          <Table.Td>{row.source}</Table.Td>
                          <Table.Td className={classes.mono}>{row.rowsTotal}</Table.Td>
                          <Table.Td className={classes.mono}>{row.rowsNew}</Table.Td>
                          <Table.Td className={classes.mono}>{row.rowsRejected}</Table.Td>
                          <Table.Td>
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              data-testid={`core-source-open-${row.id}`}
                              onClick={() => {
                                setOpenImportId(row.id === openImportId ? null : row.id);
                              }}
                            >
                              {row.id === openImportId ? 'сховати звіт' : 'звіт'}
                            </Button>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Box>
            )}

            {openImport.data === undefined ? null : (
              <Box mt="sm" data-testid="core-source-report">
                <Text className={classes.dim}>
                  рядків <span className={classes.mono}>{openImport.data.rowsTotal}</span> · нових{' '}
                  <span className={classes.mono}>{openImport.data.rowsNew}</span> · дублів{' '}
                  <span className={classes.mono}>{openImport.data.rowsDuplicate}</span> · відхилено{' '}
                  <span className={classes.mono}>{openImport.data.rowsRejected}</span>
                </Text>
                {openImport.data.rejections.length === 0 ? (
                  <Text className={classes.dim} data-testid="core-source-report-clean">
                    Жодного відхиленого рядка.
                  </Text>
                ) : (
                  <Table mt={4} fz="xs" data-testid="core-source-rejections">
                    <Table.Tbody>
                      {openImport.data.rejections.map((rejection) => (
                        <Table.Tr key={`${String(rejection.row)}-${rejection.keyword}`}>
                          <Table.Td className={classes.mono}>{rejection.row}</Table.Td>
                          <Table.Td>{rejection.keyword}</Table.Td>
                          <Table.Td className={classes.warn}>
                            {REJECTION_LABELS[rejection.reason] ?? rejection.reason}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Box>
            )}

            {report === null ? null : (
              <Box mt="sm" data-testid="core-report">
                <Group gap="lg">
                  <Text className={classes.dim}>
                    рядків: <span className={classes.mono}>{report.rowsTotal}</span>
                  </Text>
                  <Text className={classes.dim}>
                    нових:{' '}
                    <span className={classes.mono} data-testid="report-new">
                      {report.rowsNew}
                    </span>
                  </Text>
                  <Text className={classes.dim}>
                    дублів:{' '}
                    <span className={classes.mono} data-testid="report-duplicate">
                      {report.rowsDuplicate}
                    </span>
                  </Text>
                  <Text className={classes.dim}>
                    відхилено:{' '}
                    <span className={classes.mono} data-testid="report-rejected">
                      {report.rowsRejected}
                    </span>
                  </Text>
                  <Text className={classes.dim}>
                    потрібно сторінок:{' '}
                    <span className={classes.mono} data-testid="report-newpages">
                      {report.newPages}
                    </span>
                  </Text>
                </Group>

                {report.rejections.length === 0 ? null : (
                  <Table mt={8} data-testid="report-rejections">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>рядок</Table.Th>
                        <Table.Th>ключ</Table.Th>
                        <Table.Th>причина</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {report.rejections.map((r) => (
                        <Table.Tr key={`${String(r.row)}-${r.keyword}`}>
                          <Table.Td className={classes.mono}>{r.row}</Table.Td>
                          <Table.Td>{r.keyword || '—'}</Table.Td>
                          <Table.Td data-testid={`rejection-${String(r.row)}`}>
                            {REJECTION_LABELS[r.reason] ?? r.reason}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Box>
            )}
          </Box>

          <Box className={classes.panel} data-testid="core-gsc">
            <Text className={classes.panelTitle} mb={8}>
              Додати з Search Console
            </Text>
            <Group gap="sm" align="flex-end">
              <Select
                label="ГЕО"
                data={geoOptions}
                value={gscGeoId}
                onChange={setGscGeoId}
                data-testid="gsc-geo"
                style={{ width: 220 }}
              />
              <TextInput
                label="Мова"
                value={gscLanguage}
                onChange={(e) => {
                  setGscLanguage(e.currentTarget.value);
                }}
                data-testid="gsc-language"
                style={{ width: 90 }}
              />
              <Button
                size="xs"
                disabled={picked.size === 0}
                loading={fromGsc.isPending}
                data-testid="gsc-add"
                onClick={() => {
                  const rows = (candidates.data ?? []).filter((c) => picked.has(c.query));
                  fromGsc.mutate({
                    ownerType,
                    ownerId,
                    geoId: gscGeoId ?? '',
                    language: gscLanguage,
                    queries: rows.map((c) => ({
                      query: c.query,
                      clicks: c.clicks,
                      impressions: c.impressions,
                      position: Number(c.position.toFixed(1)),
                      ...(c.page ? { page: c.page } : {}),
                    })),
                  });
                }}
              >
                Додати вибрані ({picked.size})
              </Button>
            </Group>

            {fromGsc.error === null ? null : (
              <Text className={classes.danger} mt={8} data-testid="gsc-error">
                {publicMessageOf(fromGsc.error)}
              </Text>
            )}

            {candidates.error === null || candidates.error === undefined ? null : (
              <Text className={classes.danger} mt={8} data-testid="gsc-candidates-error">
                {publicMessageOf(candidates.error)}
              </Text>
            )}

            {candidates.data === undefined ? null : (
              <Table mt={8} data-testid="gsc-candidates">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th />
                    <Table.Th>запит</Table.Th>
                    <Table.Th>кліки</Table.Th>
                    <Table.Th>покази</Table.Th>
                    <Table.Th>позиція</Table.Th>
                    <Table.Th>сторінка</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {candidates.data.map((c) => (
                    <Table.Tr key={c.query} data-testid={`gsc-row-${c.query}`}>
                      <Table.Td>
                        <Checkbox
                          checked={picked.has(c.query)}
                          disabled={c.inCore}
                          data-testid={`gsc-pick-${c.query}`}
                          onChange={(e) => {
                            // Read the event before the updater runs: React calls the
                            // updater during the next render, by which point
                            // `e.currentTarget` is null and the whole screen throws.
                            const { checked } = e.currentTarget;
                            setPicked((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(c.query);
                              else next.delete(c.query);
                              return next;
                            });
                          }}
                        />
                      </Table.Td>
                      <Table.Td>
                        {c.query}
                        {c.inCore ? (
                          <Badge size="xs" ml={6} data-testid={`gsc-incore-${c.query}`}>
                            уже в ядрі
                          </Badge>
                        ) : null}
                      </Table.Td>
                      <Table.Td className={classes.mono}>{c.clicks}</Table.Td>
                      <Table.Td className={classes.mono}>{c.impressions}</Table.Td>
                      <Table.Td className={classes.mono}>{c.position.toFixed(1)}</Table.Td>
                      <Table.Td className={classes.dim}>{c.page ?? '—'}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Box>

          <DataTable
            rows={list.data?.items ?? []}
            columns={columns}
            loading={list.isPending}
            error={list.error}
            emptyMessage="Ядро порожнє. Імпортуйте CSV або додайте запити з Search Console."
            rowTestId={(row) => `kw-row-${row.keyword}`}
            testId="core-table"
            footerActions={<ExportButton dataset="keywords" zone="databases" />}
          />
        </>
      )}
    </Stack>
  );
}
